// ABOUTME: Specialized Error Boundary for order-related components
// ABOUTME: Provides order-specific error handling and recovery options

import { ErrorOutline, Home, ListAlt } from "@mui/icons-material";
import { Alert, Box, Button, Typography } from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";
import { ErrorBoundary } from "../ErrorBoundary";

interface OrderErrorFallbackProps {
  error?: Error;
  resetError: () => void;
}

const OrderErrorFallback: React.FC<OrderErrorFallbackProps> = ({
  error,
  resetError,
}) => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate("/");
  };

  const handleGoToOrders = () => {
    navigate("/orders");
    resetError();
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="400px"
      p={3}
    >
      <ErrorOutline sx={{ fontSize: 64, color: "error.main", mb: 2 }} />

      <Typography variant="h5" gutterBottom>
        Order Loading Error
      </Typography>

      <Typography
        variant="body1"
        color="text.secondary"
        textAlign="center"
        mb={3}
      >
        There was a problem loading the order information. This could be due to
        a network issue or the order may no longer exist.
      </Typography>

      {process.env.NODE_ENV === "development" && error && (
        <Alert severity="error" sx={{ mb: 2, maxWidth: "100%" }}>
          <Typography
            variant="body2"
            component="pre"
            sx={{ fontSize: "0.75rem" }}
          >
            {error.message}
          </Typography>
        </Alert>
      )}

      <Box display="flex" gap={2}>
        <Button
          variant="contained"
          startIcon={<ListAlt />}
          onClick={handleGoToOrders}
        >
          Back to Orders
        </Button>
        <Button variant="outlined" startIcon={<Home />} onClick={handleGoHome}>
          Go Home
        </Button>
      </Box>
    </Box>
  );
};

interface Props {
  children: React.ReactNode;
}

export const OrderErrorBoundary: React.FC<Props> = ({ children }) => {
  return (
    <ErrorBoundary
      fallback={
        <OrderErrorFallback resetError={() => window.location.reload()} />
      }
    >
      {children}
    </ErrorBoundary>
  );
};
